// Finished Goods / Semi-finished / Trading
export const FG_TYPES  = ['ZFG', 'ZSFG', 'ZTFG'];
// Raw Materials (direct + indirect + MRO + stocking variants)
export const RM_TYPES  = ['ZDRM', 'ZIRM', 'ZVM', 'ZVA', 'ZVNM', 'ZVNA', 'ZVNP', 'ZVP', 'ZNVM', 'ZNVA', 'ZNVP', 'ZSPR', 'ZFUE', 'ZSCR', 'ZRTP', 'ZPKG', 'ZPIP', 'ZSER', 'ZAST', 'ZBM'];
// All relevant types
export const ALL_TYPES = [...new Set([...FG_TYPES, ...RM_TYPES])];

// Mandatory matrix: ONLY these fields are mandatory, and only for the listed SAP
// material types. Every other field is optional. Keep in sync with the backend
// copy in backend/schema/fields.js.
export const MANDATORY_MATRIX = {
  // Mandatory for every material type
  material_type:             'all',
  material_group:            'all',
  procurement_type:          'all',
  price_control_indicator:   'all',
  valuation_class:           'all',
  valuation_class_prj_stock: 'all',
  industry_sector:           'all',
  plant:                     'all',
  storage_location:          'all',
  profit_center:             'all',
  tax_classification:        'all',
  gen_item_category_group:   'all',
  item_category_group:       'all',
  availability_check:        'all',
  transport_group:           'all',
  loading_group:             'all',
  country_of_origin:         'all',
  control_code:              'all',
  base_uom:                  'all',
  weight_unit:               'all',
  mrp_type:                  'all',
  lot_size:                  'all',
  inspection_type:           'all',
  // Type-specific
  valuation_type:        ['ZIRM', 'ZDRM'],
  sales_organization:    FG_TYPES,
  distribution_channel:  FG_TYPES,
  division:              FG_TYPES,
  acct_assignment_group: FG_TYPES,
  material_group_1:      FG_TYPES,
  material_group_2:      FG_TYPES,
  material_group_3:      FG_TYPES,
  material_group_4:      FG_TYPES,
  material_group_5:      FG_TYPES,
  material_number:       ['ZIRM', 'ZDRM', 'ZFG', 'ZSFG', 'ZTFG'],
};

/**
 * Returns true if the field is mandatory given the selected material type.
 * A scope of 'all' means mandatory for every material type.
 */
export const isFieldRequired = (field, materialType) => {
  const scope = MANDATORY_MATRIX[field.key];
  if (scope === 'all') return true;
  return !!scope && !!materialType && scope.includes(materialType);
};

// The SAP material types the Material Number is mandatory for. Same list the
// checker enforces, so the request form and the checker never disagree.
export const MATERIAL_NUMBER_TYPES = MANDATORY_MATRIX.material_number;

/**
 * True when the Material Number applies to the given material type — i.e. it
 * is mandatory for that type. Shared by the single-entry form, the bulk upload
 * and the checker so all three read the same rule.
 */
export const isMaterialNumberRequired = materialType =>
  isFieldRequired({ key: 'material_number' }, String(materialType || '').trim().toUpperCase());

export const MATERIAL_TABS = [
  {
    id: 'general',
    label: 'General',
    fields: [
      { key: 'material_type',         label: 'Material Type' },
      { key: 'material_number',       label: 'Material Number' },
      { key: 'industry_sector', label: 'Industry Sector', type: 'searchselect', options: [
        { code: '1', description: 'Retail' },
        { code: 'A', description: 'Plant engin./construction' },
        { code: 'C', description: 'Chemical industry' },
        { code: 'M', description: 'Mechanical engineering' },
        { code: 'P', description: 'Pharmaceuticals' },
        { code: 'T', description: 'Tyre Industry' },
      ]},
      { key: 'plant',                 label: 'Plant' },
      { key: 'storage_location',      label: 'Storage Location' },
      { key: 'valuation_type',        label: 'Valuation Type' },
      { key: 'sales_organization',    label: 'Sales Organization' },
      { key: 'distribution_channel',  label: 'Distribution Channel' },
      { key: 'division',              label: 'Division' },
      { key: 'profit_center',         label: 'Profit Center' },
      { key: 'material_stats_group',  label: 'Material Stats Group' },
    ],
  },
  {
    id: 'basic_data_1',
    label: 'Basic Data 1',
    fields: [
      { key: 'material_description',          label: 'Material Description' },
      { key: 'base_uom',                      label: 'Base UOM' },
      { key: 'material_group',                label: 'Material Group' },
      { key: 'old_material_number',           label: 'Old Material Number' },
      { key: 'cross_plant_material_status',   label: 'Cross-Plant Material Status' },
      { key: 'cross_plant_valid_from',        label: 'Date from which the cross-plant status is valid', type: 'date' },
      { key: 'basic_material',                label: 'Basic Material' },
      { key: 'packaging_material_group',      label: 'Packaging Material Group' },
    ],
  },
  {
    id: 'basic_data_2',
    label: 'Basic Data 2',
    fields: [
      { key: 'gross_weight',        label: 'Gross Weight',        type: 'number'},
      { key: 'net_weight',          label: 'Net Weight',          type: 'number'},
      { key: 'weight_unit',         label: 'Weight Unit' },
      { key: 'weight_unit_iso',     label: 'Unit of Weight in ISO Code' },
      { key: 'volume',              label: 'Volume',              type: 'number' },
      { key: 'volume_unit',         label: 'Volume Unit' },
      { key: 'size_dimensions',     label: 'Size/Dimensions' },
    ],
  },
  {
    id: 'classification',
    label: 'Classification',
    fields: [
      { key: 'industry_desc', label: 'Industry Desc' },
      { key: 'class_type',     label: 'Class Type' },
      { key: 'class',          label: 'Class' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales Views',
    fields: [
      { key: 'sales_unit',                 label: 'Sales Unit' },
      { key: 'denominator',                label: 'Denominator',          type: 'number' },
      { key: 'numerator',                  label: 'Numerator',            type: 'number' },
      { key: 'cross_dist_chain_status',    label: 'Cross-distribution-chain Material Status' },
      { key: 'dist_chain_mat_status',      label: 'Dist Chain Mat Status' },
      { key: 'dist_chain_valid_from',      label: 'Status Valid From',    type: 'date' },
      { key: 'tax_classification',         label: 'Tax Classification' },
      { key: 'delivering_plant',           label: 'Delivering Plant' },
      { key: 'material_statistics_group',  label: 'Material Statistics Group' },
      { key: 'item_category_group', label: 'Item Category Group', type: 'searchselect', options: [
        { code: '0001', description: 'Make-to-order' },
        { code: '0002', description: 'Configuration' },
        { code: '0004', description: 'Make to Order/Assem.' },
        { code: '0005', description: 'Partial Billing' },
        { code: 'ALEN', description: 'ALE Standard Item' },
        { code: 'ALES', description: 'ALE Third-Party Item' },
        { code: 'BADS', description: 'Subcontracting (DS)' },
        { code: 'BANC', description: 'Indiv.Purchase Order' },
        { code: 'BANS', description: 'Third-party Item' },
        { code: 'CB01', description: 'Credit Memo' },
        { code: 'CB02', description: 'Ret. Pack. Delivery' },
        { code: 'CB04', description: 'Make to Order' },
        { code: 'CBNA', description: '3rd Party SO w/o SN' },
        { code: 'CBOR', description: '3rd Party SO w. SN' },
        { code: 'CBUK', description: 'Bought-In' },
        { code: 'DIEN', description: 'Service w/ Delivery' },
        { code: 'ERLA', description: 'Structure/Mat. Above' },
        { code: 'ERLB', description: 'Structure/Mat. Above' },
        { code: 'GG01', description: 'Config. SW Product' },
        { code: 'GG02', description: 'Config. Service' },
        { code: 'GG04', description: 'Config. Bundle' },
        { code: 'GG05', description: 'Product List' },
        { code: 'LEAS', description: 'Leasing' },
        { code: 'LEER', description: 'Empties' },
        { code: 'LEIC', description: 'Service w/o Del.Conf' },
        { code: 'LEIH', description: 'Returnable Packaging' },
        { code: 'LEIS', description: 'Service w/o Delivery' },
        { code: 'LO01', description: 'Leas. Early C.T. Opt' },
        { code: 'LO02', description: 'Leasng Return Option' },
        { code: 'LO03', description: 'Leas. Early P Option' },
        { code: 'LO04', description: 'Leasing Purch. Optn' },
        { code: 'LO06', description: 'Leas. Renewal Option' },
        { code: 'LO08', description: 'Leas. Renewal Option' },
        { code: 'LO09', description: 'Lsg. Invoice for Opt' },
        { code: 'LO11', description: 'Leas. E C.T. Opt woV' },
        { code: 'LO12', description: 'Leasg Retn Optn w/oV' },
        { code: 'LO13', description: 'Leas. E. P Optn w/oV' },
        { code: 'LO14', description: 'Leasg Pur. Opt. w/oV' },
        { code: 'LO16', description: 'Leasg Renwl Opt w/oV' },
        { code: 'LO18', description: 'Leasg Renwl Opt w/oV' },
        { code: 'LO19', description: 'Ls. Inv. for Opt woV' },
        { code: 'LUMB', description: 'Structure/Mat. Below' },
        { code: 'LUMF', description: 'Structure/Mat. Below' },
        { code: 'NLAG', description: 'Non-stock Material' },
        { code: 'NORM', description: 'Standard item' },
        { code: 'PBOS', description: 'Bill of services' },
        { code: 'PRRP', description: 'Subscr. Usage Only' },
        { code: 'PRSL', description: 'Subscr. Recrrg Sales' },
        { code: 'PRSM', description: 'Subscr., Multi AVC' },
        { code: 'PRSR', description: 'Subscr., Recurrent' },
        { code: 'PRSV', description: 'Subscription Fee' },
        { code: 'SAMM', description: 'Generic material' },
        { code: 'SCNP', description: 'Service Cntrct Prod.' },
        { code: 'SERV', description: 'Service' },
        { code: 'SQBG', description: '' },
        { code: 'SRVE', description: 'Expense Item' },
        { code: 'SRVM', description: 'Service Material' },
        { code: 'SRVP', description: 'Service Product' },
        { code: 'SRVT', description: 'Service Tool' },
        { code: 'VCIT', description: 'Value Contract Item' },
        { code: 'VERB', description: 'Packaging Billing' },
        { code: 'VERP', description: 'Packaging' },
        { code: 'VMS0', description: 'VMS Vehicle' },
        { code: 'VOLL', description: 'Full product' },
        { code: 'WERT', description: 'Val.Item Deliv.Note' },
        { code: 'WMPP', description: 'Raw materials WM-PP' },
      ]},
      { key: 'material_pricing_group',     label: 'Material Pricing Group' },
    ],
  },
  {
    id: 'purchasing',
    label: 'Purchasing',
    fields: [
      { key: 'gen_item_category_group', label: 'General Item Category Group', type: 'searchselect', options: [
        { code: '0001', description: 'Make-to-order' },
        { code: '0002', description: 'Configuration' },
        { code: '0004', description: 'Make to Order/Assem.' },
        { code: '0005', description: 'Partial Billing' },
        { code: 'ALEN', description: 'ALE Standard Item' },
        { code: 'ALES', description: 'ALE Third-Party Item' },
        { code: 'BADS', description: 'Subcontracting (DS)' },
        { code: 'BANC', description: 'Indiv.Purchase Order' },
        { code: 'BANS', description: 'Third-party Item' },
        { code: 'CB01', description: 'Credit Memo' },
        { code: 'CB02', description: 'Ret. Pack. Delivery' },
        { code: 'CB04', description: 'Make to Order' },
        { code: 'CBNA', description: '3rd Party SO w/o SN' },
        { code: 'CBOR', description: '3rd Party SO w. SN' },
        { code: 'CBUK', description: 'Bought-In' },
        { code: 'DIEN', description: 'Service w/ Delivery' },
        { code: 'ERLA', description: 'Structure/Mat. Above' },
        { code: 'ERLB', description: 'Structure/Mat. Above' },
        { code: 'GG01', description: 'Config. SW Product' },
        { code: 'GG02', description: 'Config. Service' },
        { code: 'GG04', description: 'Config. Bundle' },
        { code: 'GG05', description: 'Product List' },
        { code: 'LEAS', description: 'Leasing' },
        { code: 'LEER', description: 'Empties' },
        { code: 'LEIC', description: 'Service w/o Del.Conf' },
        { code: 'LEIH', description: 'Returnable Packaging' },
        { code: 'LEIS', description: 'Service w/o Delivery' },
        { code: 'LO01', description: 'Leas. Early C.T. Opt' },
        { code: 'LO02', description: 'Leasng Return Option' },
        { code: 'LO03', description: 'Leas. Early P Option' },
        { code: 'LO04', description: 'Leasing Purch. Optn' },
        { code: 'LO06', description: 'Leas. Renewal Option' },
        { code: 'LO08', description: 'Leas. Renewal Option' },
        { code: 'LO09', description: 'Lsg. Invoice for Opt' },
        { code: 'LO11', description: 'Leas. E C.T. Opt woV' },
        { code: 'LO12', description: 'Leasg Retn Optn w/oV' },
        { code: 'LO13', description: 'Leas. E. P Optn w/oV' },
        { code: 'LO14', description: 'Leasg Pur. Opt. w/oV' },
        { code: 'LO16', description: 'Leasg Renwl Opt w/oV' },
        { code: 'LO18', description: 'Leasg Renwl Opt w/oV' },
        { code: 'LO19', description: 'Ls. Inv. for Opt woV' },
        { code: 'LUMB', description: 'Structure/Mat. Below' },
        { code: 'LUMF', description: 'Structure/Mat. Below' },
        { code: 'NLAG', description: 'Non-stock Material' },
        { code: 'NORM', description: 'Standard item' },
        { code: 'PBOS', description: 'Bill of services' },
        { code: 'PRRP', description: 'Subscr. Usage Only' },
        { code: 'PRSL', description: 'Subscr. Recrrg Sales' },
        { code: 'PRSM', description: 'Subscr., Multi AVC' },
        { code: 'PRSR', description: 'Subscr., Recurrent' },
        { code: 'PRSV', description: 'Subscription Fee' },
        { code: 'SAMM', description: 'Generic material' },
        { code: 'SCNP', description: 'Service Cntrct Prod.' },
        { code: 'SERV', description: 'Service' },
        { code: 'SQBG', description: '' },
        { code: 'SRVE', description: 'Expense Item' },
        { code: 'SRVM', description: 'Service Material' },
        { code: 'SRVP', description: 'Service Product' },
        { code: 'SRVT', description: 'Service Tool' },
        { code: 'VCIT', description: 'Value Contract Item' },
        { code: 'VERB', description: 'Packaging Billing' },
        { code: 'VERP', description: 'Packaging' },
        { code: 'VMS0', description: 'VMS Vehicle' },
        { code: 'VOLL', description: 'Full product' },
        { code: 'WERT', description: 'Val.Item Deliv.Note' },
        { code: 'WMPP', description: 'Raw materials WM-PP' },
      ]},
      { key: 'acct_assignment_group', label: 'Acct Assignment Group', type: 'searchselect', options: [
        { code: '01', description: 'Trading Goods' },
        { code: '02', description: 'Performances' },
        { code: '03', description: 'Finished Products' },
        { code: '04', description: 'Scrap - Process' },
        { code: '05', description: 'Scrap - Non Process' },
        { code: '06', description: 'Assets' },
        { code: '07', description: 'Service' },
        { code: '08', description: 'Promotional sales' },
        { code: 'X1', description: 'SP-Time' },
        { code: 'Y2', description: 'Returnable goods' },
      ]},
      { key: 'availability_check', label: 'Availability Check', type: 'searchselect', options: [
        { code: '01', description: 'Daily requirements' },
        { code: '02', description: 'Individ.requirements' },
        { code: 'CH', description: 'Batches' },
        { code: 'DR', description: '' },
        { code: 'KP', description: 'No check' },
        { code: 'NC', description: 'No ATP Check' },
        { code: 'SP', description: 'Stock and Plan. Rec.' },
        { code: 'SR', description: 'Stock and Rel. Rec.' },
        { code: 'ST', description: 'Consider Only Stock' },
        { code: 'ZP', description: 'Batches' },
      ]},
      { key: 'transport_group', label: 'Transport Group', type: 'searchselect', options: [
        { code: '0001', description: 'On pallets' },
        { code: '0002', description: 'In liquid form' },
        { code: '0003', description: 'Container' },
        { code: '0004', description: 'Parcel' },
        { code: '0005', description: 'Bulk Product' },
      ]},
      { key: 'loading_group', label: 'Loading Group', type: 'searchselect', options: [
        { code: '0001', description: 'Crane' },
        { code: '0002', description: 'Forklift' },
        { code: '0003', description: 'Manual' },
      ]},
      { key: 'packaging_material_type',        label: 'Packaging Material Type' },
      { key: 'allowed_packaging_weight',       label: 'Allowed Packaging Weight',       type: 'number' },
      { key: 'allowed_packaging_weight_unit',  label: 'Unit of Weight (Allowed Packaging Weight)' },
      { key: 'allowed_packaging_volume',       label: 'Allowed Packaging Volume',       type: 'number' },
      { key: 'allowed_packaging_volume_unit',  label: 'Volume Unit (Allowed Packaging Volume)' },
      { key: 'material_group_1',               label: 'Material Group 1' },
      { key: 'material_group_2',               label: 'Material Group 2' },
      { key: 'material_group_3',               label: 'Material Group 3' },
      { key: 'material_group_4',               label: 'Material Group 4' },
      { key: 'material_group_5',               label: 'Material Group 5' },
      { key: 'commodity_code',                 label: 'Commodity Code' },
      { key: 'export_import_group',            label: 'Export/Import Group' },
      { key: 'control_code',                   label: 'Control Code' },
      { key: 'country_of_origin', label: 'Country of Origin', type: 'searchselect', options: [
        { code: 'AD', description: 'Andorra' },
        { code: 'AE', description: 'Utd.Arab Emir.' },
        { code: 'AF', description: 'Afghanistan' },
        { code: 'AG', description: 'Antigua/Barbuda' },
        { code: 'AI', description: 'Anguilla' },
        { code: 'AL', description: 'Albania' },
        { code: 'AM', description: 'Armenia' },
        { code: 'AO', description: 'Angola' },
        { code: 'AQ', description: 'Antarctica' },
        { code: 'AR', description: 'Argentina' },
        { code: 'AS', description: 'Samoa, America' },
        { code: 'AT', description: 'Austria' },
        { code: 'AU', description: 'Australia' },
        { code: 'AW', description: 'Aruba' },
        { code: 'AX', description: 'Aland Islands' },
        { code: 'AZ', description: 'Azerbaijan' },
        { code: 'BA', description: 'Bosnia-Herz.' },
        { code: 'BB', description: 'Barbados' },
        { code: 'BD', description: 'Bangladesh' },
        { code: 'BE', description: 'Belgium' },
        { code: 'BF', description: 'Burkina Faso' },
        { code: 'BG', description: 'Bulgaria' },
        { code: 'BH', description: 'Bahrain' },
        { code: 'BI', description: 'Burundi' },
        { code: 'BJ', description: 'Benin' },
        { code: 'BL', description: 'St Barthelemy' },
        { code: 'BM', description: 'Bermuda' },
        { code: 'BN', description: 'Brunei Daruss.' },
        { code: 'BO', description: 'Bolivia' },
        { code: 'BQ', description: 'Bonaire, Saba' },
        { code: 'BR', description: 'Brazil' },
        { code: 'BS', description: 'Bahamas' },
        { code: 'BT', description: 'Bhutan' },
        { code: 'BV', description: 'Bouvet Islands' },
        { code: 'BW', description: 'Botswana' },
        { code: 'BY', description: 'Belarus' },
        { code: 'BZ', description: 'Belize' },
        { code: 'CA', description: 'Canada' },
        { code: 'CC', description: 'Cocos Islands' },
        { code: 'CD', description: 'Dem. Rep. Congo' },
        { code: 'CF', description: 'CAR' },
        { code: 'CG', description: 'Rep.of Congo' },
        { code: 'CH', description: 'Switzerland' },
        { code: 'CI', description: "Cote d'Ivoire" },
        { code: 'CK', description: 'Cook Islands' },
        { code: 'CL', description: 'Chile' },
        { code: 'CM', description: 'Cameroon' },
        { code: 'CN', description: 'China' },
        { code: 'CO', description: 'Colombia' },
        { code: 'CR', description: 'Costa Rica' },
        { code: 'CU', description: 'Cuba' },
        { code: 'CV', description: 'Cabo Verde' },
        { code: 'CW', description: 'Curaçao' },
        { code: 'CX', description: 'Christmas Islnd' },
        { code: 'CY', description: 'Cyprus' },
        { code: 'CZ', description: 'Czech Republic' },
        { code: 'DE', description: 'Germany' },
        { code: 'DJ', description: 'Djibouti' },
        { code: 'DK', description: 'Denmark' },
        { code: 'DM', description: 'Dominica' },
        { code: 'DO', description: 'Dominican Rep.' },
        { code: 'DZ', description: 'Algeria' },
        { code: 'EC', description: 'Ecuador' },
        { code: 'EE', description: 'Estonia' },
        { code: 'EG', description: 'Egypt' },
        { code: 'EH', description: 'West Sahara' },
        { code: 'ER', description: 'Eritrea' },
        { code: 'ES', description: 'Spain' },
        { code: 'ET', description: 'Ethiopia' },
        { code: 'FI', description: 'Finland' },
        { code: 'FJ', description: 'Fiji' },
        { code: 'FK', description: 'Falkland Islnds' },
        { code: 'FM', description: 'Micronesia' },
        { code: 'FO', description: 'Faroe Islands' },
        { code: 'FR', description: 'France' },
        { code: 'GA', description: 'Gabon' },
        { code: 'GB', description: 'United Kingdom' },
        { code: 'GD', description: 'Grenada' },
        { code: 'GE', description: 'Georgia' },
        { code: 'GF', description: 'French Guayana' },
        { code: 'GG', description: 'Guernsey' },
        { code: 'GH', description: 'Ghana' },
        { code: 'GI', description: 'Gibraltar' },
        { code: 'GL', description: 'Greenland' },
        { code: 'GM', description: 'Gambia' },
        { code: 'GN', description: 'Guinea' },
        { code: 'GP', description: 'Guadeloupe' },
        { code: 'GQ', description: 'Equatorial Guin' },
        { code: 'GR', description: 'Greece' },
        { code: 'GS', description: 'S. Sandwich Ins' },
        { code: 'GT', description: 'Guatemala' },
        { code: 'GU', description: 'Guam' },
        { code: 'GW', description: 'Guinea-Bissau' },
        { code: 'GY', description: 'Guyana' },
        { code: 'HK', description: 'Hong Kong' },
        { code: 'HM', description: 'Heard/McDon.Isl' },
        { code: 'HN', description: 'Honduras' },
        { code: 'HR', description: 'Croatia' },
        { code: 'HT', description: 'Haiti' },
        { code: 'HU', description: 'Hungary' },
        { code: 'ID', description: 'Indonesia' },
        { code: 'IE', description: 'Ireland' },
        { code: 'IL', description: 'Israel' },
        { code: 'IM', description: 'Isle of Man' },
        { code: 'IN', description: 'India' },
        { code: 'IO', description: 'Brit.Ind.Oc.Ter' },
        { code: 'IQ', description: 'Iraq' },
        { code: 'IR', description: 'Iran' },
        { code: 'IS', description: 'Iceland' },
        { code: 'IT', description: 'Italy' },
        { code: 'JE', description: 'Jersey' },
        { code: 'JM', description: 'Jamaica' },
        { code: 'JO', description: 'Jordan' },
        { code: 'JP', description: 'Japan' },
        { code: 'KE', description: 'Kenya' },
        { code: 'KG', description: 'Kyrgyzstan' },
        { code: 'KH', description: 'Cambodia' },
        { code: 'KI', description: 'Kiribati' },
        { code: 'KM', description: 'Comoros' },
        { code: 'KN', description: 'St Kitts&Nevis' },
        { code: 'KP', description: 'North Korea' },
        { code: 'KR', description: 'South Korea' },
        { code: 'KW', description: 'Kuwait' },
        { code: 'KY', description: 'Cayman Islands' },
        { code: 'KZ', description: 'Kazakhstan' },
        { code: 'LA', description: 'Laos' },
        { code: 'LB', description: 'Lebanon' },
        { code: 'LC', description: 'St. Lucia' },
        { code: 'LI', description: 'Liechtenstein' },
        { code: 'LK', description: 'Sri Lanka' },
        { code: 'LR', description: 'Liberia' },
        { code: 'LS', description: 'Lesotho' },
        { code: 'LT', description: 'Lithuania' },
        { code: 'LU', description: 'Luxembourg' },
        { code: 'LV', description: 'Latvia' },
        { code: 'LY', description: 'Libya' },
        { code: 'MA', description: 'Morocco' },
        { code: 'MC', description: 'Monaco' },
        { code: 'MD', description: 'Moldova' },
        { code: 'ME', description: 'Montenegro' },
        { code: 'MF', description: 'Saint Martin' },
        { code: 'MG', description: 'Madagascar' },
        { code: 'MH', description: 'Marshall Islnds' },
        { code: 'MK', description: 'North Macedonia' },
        { code: 'ML', description: 'Mali' },
        { code: 'MM', description: 'Myanmar' },
        { code: 'MN', description: 'Mongolia' },
        { code: 'MO', description: 'Macao' },
        { code: 'MP', description: 'N.Mariana Islnd' },
        { code: 'MQ', description: 'Martinique' },
        { code: 'MR', description: 'Mauretania' },
        { code: 'MS', description: 'Montserrat' },
        { code: 'MT', description: 'Malta' },
        { code: 'MU', description: 'Mauritius' },
        { code: 'MV', description: 'Maldives' },
        { code: 'MW', description: 'Malawi' },
        { code: 'MX', description: 'Mexico' },
        { code: 'MY', description: 'Malaysia' },
        { code: 'MZ', description: 'Mozambique' },
        { code: 'NA', description: 'Namibia' },
        { code: 'NC', description: 'New Caledonia' },
        { code: 'NE', description: 'Niger' },
        { code: 'NF', description: 'Norfolk Islands' },
        { code: 'NG', description: 'Nigeria' },
        { code: 'NI', description: 'Nicaragua' },
        { code: 'NL', description: 'Netherlands' },
        { code: 'NO', description: 'Norway' },
        { code: 'NP', description: 'Nepal' },
        { code: 'NR', description: 'Nauru' },
        { code: 'NU', description: 'Niue' },
        { code: 'NZ', description: 'New Zealand' },
        { code: 'OM', description: 'Oman' },
        { code: 'PA', description: 'Panama' },
        { code: 'PE', description: 'Peru' },
        { code: 'PF', description: 'Frenc.Polynesia' },
        { code: 'PG', description: 'Pap. New Guinea' },
        { code: 'PH', description: 'Philippines' },
        { code: 'PK', description: 'Pakistan' },
        { code: 'PL', description: 'Poland' },
        { code: 'PM', description: 'St.Pier,Miquel.' },
        { code: 'PN', description: 'Pitcairn Islnds' },
        { code: 'PR', description: 'Puerto Rico' },
        { code: 'PS', description: 'Palestine' },
        { code: 'PT', description: 'Portugal' },
        { code: 'PW', description: 'Palau' },
        { code: 'PY', description: 'Paraguay' },
        { code: 'QA', description: 'Qatar' },
        { code: 'RE', description: 'Reunion' },
        { code: 'RO', description: 'Romania' },
        { code: 'RS', description: 'Serbia' },
        { code: 'RU', description: 'Russian Fed.' },
        { code: 'RW', description: 'Rwanda' },
        { code: 'SA', description: 'Saudi Arabia' },
        { code: 'SB', description: 'Solomon Islands' },
        { code: 'SC', description: 'Seychelles' },
        { code: 'SD', description: 'Sudan' },
        { code: 'SE', description: 'Sweden' },
        { code: 'SG', description: 'Singapore' },
        { code: 'SH', description: 'Saint Helena' },
        { code: 'SI', description: 'Slovenia' },
        { code: 'SJ', description: 'Svalbard' },
        { code: 'SK', description: 'Slovakia' },
        { code: 'SL', description: 'Sierra Leone' },
        { code: 'SM', description: 'San Marino' },
        { code: 'SN', description: 'Senegal' },
        { code: 'SO', description: 'Somalia' },
        { code: 'SR', description: 'Suriname' },
        { code: 'SS', description: 'South Sudan' },
        { code: 'ST', description: 'S.Tome,Principe' },
        { code: 'SV', description: 'El Salvador' },
        { code: 'SX', description: 'Sint Maarten' },
        { code: 'SY', description: 'Syria' },
        { code: 'SZ', description: 'Eswatini' },
        { code: 'TC', description: 'Turksh Caicosin' },
        { code: 'TD', description: 'Chad' },
        { code: 'TF', description: 'French S.Territ' },
        { code: 'TG', description: 'Togo' },
        { code: 'TH', description: 'Thailand' },
        { code: 'TJ', description: 'Tajikistan' },
        { code: 'TK', description: 'Tokelau Islands' },
        { code: 'TL', description: 'Timor-Leste' },
        { code: 'TM', description: 'Turkmenistan' },
        { code: 'TN', description: 'Tunisia' },
        { code: 'TO', description: 'Tonga' },
        { code: 'TR', description: 'Türkiye' },
        { code: 'TT', description: 'Trinidad,Tobago' },
        { code: 'TV', description: 'Tuvalu' },
        { code: 'TW', description: 'Taiwan' },
        { code: 'TZ', description: 'Tanzania' },
        { code: 'UA', description: 'Ukraine' },
        { code: 'UG', description: 'Uganda' },
        { code: 'UM', description: 'Minor Outl.Isl.' },
        { code: 'US', description: 'USA' },
        { code: 'UY', description: 'Uruguay' },
        { code: 'UZ', description: 'Uzbekistan' },
        { code: 'VA', description: 'Vatican City' },
        { code: 'VC', description: 'St. Vincent' },
        { code: 'VE', description: 'Venezuela' },
        { code: 'VG', description: 'Brit.Virgin Is.' },
        { code: 'VI', description: 'Amer.Virgin Is.' },
        { code: 'VN', description: 'Vietnam' },
        { code: 'VU', description: 'Vanuatu' },
        { code: 'WF', description: 'Wallis,Futuna' },
        { code: 'WS', description: 'Samoa' },
        { code: 'YE', description: 'Yemen' },
        { code: 'YT', description: 'Mayotte' },
        { code: 'ZA', description: 'South Africa' },
        { code: 'ZM', description: 'Zambia' },
        { code: 'ZW', description: 'Zimbabwe' },
      ]},
      { key: 'region_of_origin',               label: 'Region of Origin' },
      { key: 'purchasing_value_key',           label: 'Purchasing Value Key' },
      { key: 'purchasing_group',               label: 'Purchasing Group' },
      { key: 'batch_management',               label: 'Batch Mgmt' },
      { key: 'gr_processing_time',             label: 'GR Proc Time',        type: 'number' },
      { key: 'quota_arrangement_usage',        label: 'Quota Arrangement Usage' },
    ],
  },
  {
    id: 'mrp_1',
    label: 'MRP View 1',
    fields: [
      { key: 'mrp_type', label: 'MRP Type', type: 'searchselect', options: [
        { code: 'D1', description: 'DD Replenishment for Discrete and Process Industry' },
        { code: 'D2', description: 'DD Replenishment for Repetitive Manufacturing' },
        { code: 'M0', description: 'MPS, FCST Consumption, No Firming' },
        { code: 'M1', description: 'MPS, FCST Consumption, Auto Firming, New Ords after PTF' },
        { code: 'M2', description: 'MPS, FCST Consumption, Auto Firming, No New Ords in PTF' },
        { code: 'M3', description: 'MPS, FCST Consumption, Manual Firming, New Ords after PTF' },
        { code: 'M4', description: 'MPS, FCST Consumption, Manual Firming, No New Ords in PTF' },
        { code: 'ND', description: 'No planning' },
        { code: 'P1', description: 'Forecast Consumption, Auto Firming, New Ords after PTF' },
        { code: 'P2', description: 'Forecast Consumption, Auto Firming, No New Ords in PTF' },
        { code: 'P3', description: 'Forecast Consumption, Manual Firming, New Ords after PTF' },
        { code: 'P4', description: 'Forecast Consumption, Manual Firming, No New Ords in PTF' },
        { code: 'PD', description: 'Forecast Consumption, No Planning Time Fence' },
        { code: 'R1', description: 'Time-Phased Planning' },
        { code: 'R2', description: 'Time-Phased Planning w.auto.reord.point' },
        { code: 'RE', description: 'Replenishment plnd externally' },
        { code: 'RF', description: 'Replenish with dyn.TargetStock' },
        { code: 'RP', description: 'Replenishment' },
        { code: 'RR', description: '' },
        { code: 'RS', description: '' },
        { code: 'V1', description: 'Manual reord.point w. ext.reqs' },
        { code: 'V2', description: 'Autom. reord.point w. ext.reqs' },
        { code: 'VB', description: 'Reorder-Point Planning' },
        { code: 'VI', description: 'Vendor Managed Inventory' },
        { code: 'VM', description: 'Automatic reorder point plng' },
        { code: 'VS', description: 'Seasonal MRP' },
        { code: 'VV', description: 'Forecast-based planning' },
        { code: 'X0', description: 'W/O MRP, with BOM Explosion' },
      ]},
      { key: 'mrp_controller',        label: 'MRP Controller' },
      { key: 'mrp_group',             label: 'MRP Group' },
      { key: 'abc_indicator',         label: 'ABC Indicator' },
      { key: 'order_unit',            label: 'Order Unit' },
      { key: 'lot_size',              label: 'Lot Size' },
      { key: 'min_lot_size',          label: 'Min Lot Size',          type: 'number' },
      { key: 'max_lot_size',          label: 'Max Lot Size',          type: 'number' },
      { key: 'fixed_lot_size',        label: 'Fixed Lot Size',        type: 'number' },
      { key: 'maximum_stock_level',   label: 'Maximum Stock Level',   type: 'number' },
      { key: 'rounding_value',        label: 'Rounding Value',        type: 'number' },
      { key: 'rounding_point',        label: 'Rounding Point',        type: 'number' },
      { key: 'rounding_profile',      label: 'Rounding Profile' },
      { key: 'procurement_type',      label: 'Procurement Type' },
      { key: 'special_procurement_type', label: 'Special Procurement Type' },
      { key: 'mrp_storage_location',  label: 'Storage Location (MRP)' },
    ],
  },
  {
    id: 'mrp_2',
    label: 'MRP View 2',
    fields: [
      { key: 'planned_delivery_time',       label: 'Planned Delivery Time in Days', type: 'number' },
      { key: 'in_house_production_time',    label: 'In-house Production Time',      type: 'number' },
      { key: 'scheduling_margin_key',       label: 'Scheduling Margin Key for Floats' },
      { key: 'safety_stock',                label: 'Safety Stock',                  type: 'number' },
      { key: 'min_safety_stock',            label: 'Minimum Safety Stock',          type: 'number' },
      { key: 'coverage_profile',            label: 'Coverage Profile' },
      { key: 'planning_strategy_group',     label: 'Planning Strategy Group' },
      { key: 'consumption_mode',            label: 'Consumption Mode' },
      { key: 'consumption_period_backward', label: 'Consumption Period: Backward',  type: 'number' },
      { key: 'consumption_period_forward',  label: 'Consumption Period: Forward',   type: 'number' },
      { key: 'mixed_mrp_indicator',         label: 'Mixed MRP Indicator' },
      { key: 'total_repl_leadtime',         label: 'Total Rep. Leadtime',           type: 'number' },
      { key: 'assembly_scrap',              label: 'Assembly Scrap',                type: 'number' },
      { key: 'scrap',                       label: 'Scrap',                         type: 'number' },
      { key: 'backflush',                   label: 'Backflush' },
      { key: 'stock_determination_group',   label: 'Stock Determination Group' },
      { key: 'storage_location_ep',         label: 'Storage Loc. for EP' },
    ],
  },
  {
    id: 'mrp_3',
    label: 'MRP View 3',
    fields: [
      { key: 'repetitive_mfg_profile',      label: 'Repetitive Manufacturing Profile' },
      { key: 'dependent_req_indicator',     label: 'Dependent Requirements Indicator' },
      { key: 'req_grouping_indicator',      label: 'Indicator for Requirements Grouping' },
      { key: 'mrp_relevancy_dependent',     label: 'MRP Relevancy for Dependent Requirements' },
      { key: 'production_scheduling_profile', label: 'Production Scheduling Profile' },
      { key: 'production_supervisor',       label: 'Production Supervisor' },
      { key: 'unit_of_issue',               label: 'Unit of Issue' },
      { key: 'prod_unit',                   label: 'Prod Unit' },
      { key: 'underdelivery_tolerance',     label: 'Underdelivery Tol',             type: 'number' },
      { key: 'overdelivery_tolerance',      label: 'Overdelivery Tol',              type: 'number' },
      { key: 'batch_entry',                 label: 'Batch Entry' },
      { key: 'phys_inventory_indicator',    label: 'Phys Inventory Indicator' },
      { key: 'cc_fixed',                    label: 'CC Fixed' },
      { key: 'shelf_life',                  label: 'Shelf Life',                    type: 'number' },
      { key: 'total_shelf_life',            label: 'Total Shelf Life',              type: 'number' },
      { key: 'negative_stock',              label: 'Negative Stock' },
      { key: 'serial_number_profile',       label: 'Serial No Profile' },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting Views',
    fields: [
      { key: 'valuation_class',            label: 'Valuation Class' },
      { key: 'valuation_class_so_stock',   label: 'Valuation Class for Sales Order Stock' },
      { key: 'valuation_class_prj_stock',  label: 'Valuation Class for Project Stock' },
      { key: 'material_ledger_active',     label: 'Material Ledger Act' },
      { key: 'valuation_category',         label: 'Valuation Category' },
      { key: 'lifo_fifo_relevant',         label: 'LIFO/FIFO-Relevant' },
      { key: 'lifo_pool_number',           label: 'Pool Number for LIFO Valuation' },
      { key: 'material_related_origin',    label: 'Material-related Origin' },
      { key: 'origin_group',               label: 'Origin Group' },
    ],
  },
  {
    id: 'costing',
    label: 'Costing Views',
    fields: [
      { key: 'costing_overhead_group',      label: 'Costing Overhead Group' },
      { key: 'alternative_bom',             label: 'Alternative BOM' },
      { key: 'task_list_group_key',         label: 'Key for Task List Group' },
      { key: 'group_counter_qty_structure', label: 'Group Counter Qty Structure' },
      { key: 'price_control_indicator',     label: 'Price Control Indicator' },
      { key: 'moving_average_price',        label: 'Moving Average Price',   type: 'number' },
      { key: 'standard_price',              label: 'Standard Price',         type: 'number' },
    ],
  },
  {
    id: 'quality',
    label: 'Quality View',
    fields: [
      { key: 'qm_authorization_group',  label: 'Material Authorization Group for Activities in QM' },
      { key: 'stock_type',              label: 'Stock Type' },
      { key: 'catalog_profile',         label: 'Catalog Profile' },
      { key: 'plant_status',            label: 'Plant Status' },
      { key: 'qm_in_procurement_active', label: 'QM in Procurement Is Active' },
      { key: 'qa_control_key',          label: 'QA Control Key' },
      { key: 'inspection_setup',        label: 'Inspection Setup' },
      { key: 'certificate_type',        label: 'Certificate Type' },
      { key: 'inspection_interval',     label: 'Inspection Interval',  type: 'number' },
      { key: 'inspection_type',         label: 'Inspection Type' },
    ],
  },
];

export const MATERIAL_FIELDS = MATERIAL_TABS.flatMap(t => t.fields);

export const LABEL_BY_KEY = MATERIAL_FIELDS.reduce((acc, f) => {
  acc[f.key] = f.label;
  return acc;
}, {});
